<template>
    <div class="wrapper">
        <h2 id="title">Authorization settings</h2>

        <section v-if="requiresRight(this.$parleyAdmin.auth.getRightsList().GetSharedSecret.id)">
            <h4>Secret</h4>
            <div class="errors" v-if="$validator.errors && $validator.errors.has('sharedSecret')">
                <small v-for="(error, index) in $validator.errors.collect('sharedSecret')" v-bind:key="index">
                    {{ error }}
                </small>
            </div>
            <div class="row">
                <custom-input class="sharedSecret" label="Shared secret" name="sharedSecret" type="secret" v-model="sharedSecret" :showSecret="showSharedSecret" v-on:toggleSecret="toggleShareSecretDisplay" />
            </div>
        </section>

        <section v-if="requiresRight(this.$parleyAdmin.auth.getRightsList().AuthorizationHeadersSettings.id)">
            <h4>Settings</h4>
            <div class="errors" v-if="$validator.errors && $validator.errors.has('authorizationRequired')">
                <small v-for="(error, index) in $validator.errors.collect('authorizationRequired')" v-bind:key="index">
                    {{ error }}
                </small>
            </div>
            <div class="row">
                <custom-input label="Authorization required" name="authorizationRequired" type="toggle" v-model="authorizationRequired"/>
                <help-popover>
                    <template slot="content">
                        <p>Change the setting for requiring authorization to <code>true</code> then every user needs to authorized before using the Parley chat</p>
                        <br>
                        <p>Change the setting for requiring authorization to <code>false</code> so unauthorized users can use the chat as well</p>
                    </template>
                </help-popover>
            </div>
        </section>

        <section v-if="requiresRight(this.$parleyAdmin.auth.getRightsList().AuthorizationHeaderGenerateAndValidate.id)">
            <h4>Authorization</h4>
            <div class="row no-margin">
                <custom-input label="Authorization version" type="dropdown" :items="[
                {value: 2, label: 'v2'},
                {value: 3, label: 'v3'}
            ]" v-model="version" />
            </div>

            <p>Generate authorization header</p>
            <div class="row no-margin">
                <custom-input label="User identification" name="userIdentification" ref="userIdentification" type="text" :validationRules="{required: true}" v-model="userIdentification"/>
                <custom-input label="Secret key" name="secretKey" ref="secretKey" type="text" :validationRules="{required: true}" v-model="secretKey"/>

                <custom-input v-if="version === 3" label="Valid untill" name="validUntill" ref="validUntill" type="datetime" :validationRules="{required: true}" v-model="validUntill"/>

                <button class="primary" v-on:click="generateAuthorization()">Generate</button>
            </div>
            <div class="row">
                <custom-input class="generatedHeader" label="Generated authorization header" type="display" name="generatedHeader" v-model="generatedHeader" placeholder="Generated Header"/>
            </div>

            <p>Validate authorization header</p>
            <div class="row">
                <custom-input class="authorizationHeader" label="Authorization header" name="authorizationHeader" ref="authorizationHeader" type="text" :validationRules="{required: true}" v-model="authorizationHeader"/>
                <button class="primary" v-on:click="validateAuthorization()">Validate</button>
            </div>

            <a href="https://developers.parley.nu/docs/client-api-authorization" target="_blank">More info on Authorization?</a>
        </section>

        <section id="authorizationHeaderModal">
            <modal ref="authorizationHeaderModal">
                <div slot="modal-header">
                    Authorization header content
                </div>
                <div slot="modal-content" class="content">
                    <section>
                        <label for="userIdentification">User Identification:</label>
                        <p id="userIdentification">{{ this.authResultUserIdentification }}</p>
                    </section>
                    <section>
                        <custom-input type="display-area" label="User Encryption Key:" v-model="authResultUserEncryptionKey"/>
                    </section>
                    <section v-if="version === 3">
                        <label for="validUntill">Valid untill:</label>
                        <p id="validUntill">{{ this.validUntillFormatted }}</p>
                    </section>
                    <section>
                        <custom-input type="display-area" label="Verify hash:" v-model="authResultVerifyHash"/>
                    </section>
                </div>
                <div slot="modal-footer" class="footer">
                    <div>
                        <button class="secondary" @click="$refs['authorizationHeaderModal'].closeModal()">Close</button>
                    </div>
                </div>
            </modal>
        </section>
    </div>
</template>

<script>
import { Default } from '@/mixins/mixins';
import customInput from '@/components/additional/customInput';
import modal from '@/components/additional/modal';
import dateformat from 'dateformat';
import HelpPopover from '../additional/helpPopover';

export default {
    name: 'settingsAuthorization',
    mixins: [
        Default
    ],
    components: {
        HelpPopover,
        customInput,
        modal
    },
    inject: {
        $validator: '$validator' // Get the parent $validator instance
    },
    data() {
        return {
            dummySharedSecret: 'fakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefake',
            sharedSecret: '',
            authorizationRequired: false,
            userIdentification: '',
            secretKey: '',
            validUntill: '',
            generatedHeader: '',
            authorizationHeader: '',
            showSharedSecret: false,
            version: 2,
            doneInitialising: false,
            authResultUserEncryptionKey: '',
            authResultUserIdentification: '',
            authResultValidUntill: '',
            authResultVerifyHash: ''
        };
    },
    created: function() {
        // Get sharedSecret
        this.sharedSecret = this.dummySharedSecret;
        // TODO: Get authorizationRequired
        this.getAuthorizationRequired().then(() => { this.doneInitialising = true; });
    },
    watch: {
        authorizationRequired: function() {
            if (this.doneInitialising)
                this.saveAuthorizationRequired();
        },
        version: function() {
            this.generatedHeader = '';
        }
    },
    computed: {
        validUntillFormatted: {
            get() {
                if (this.authResultValidUntill) {
                    const date = new Date(this.authResultValidUntill * 1000);
                    return dateformat(date, 'dd-mmmm-yyyy HH:MM:ss');
                } else {
                    return this.authResultValidUntill;
                }
            }
        }
    },
    methods: {
        toggleShareSecretDisplay: function() {
            if (!this.showSharedSecret)
                this.getSharedSecret();
            else {
                this.showSharedSecret = false;
                this.sharedSecret = this.dummySharedSecret;
            }
        },
        getSharedSecret: function() {
            this.$http.get('v1.1/authorization/sharedSecret').then(response => {
                // Success callback
                this.sharedSecret = response.data.data.sharedSecret;
                this.showSharedSecret = true;
            }, response => {
                // Error callback
                let notifications = JSON.parse(response.bodyText).notifications;
                if (notifications.filter((element) => { return element.message === 'shared_secret_not_found'; }).length > 0)
                    this.$validator.errors.add({
                        field: 'sharedSecret', // Not used in the traditional way, check the error div in the <template>
                        msg: 'Shared secret not found'
                    });
                if (notifications.filter((element) => { return element.message === 'identification_not_valid'; }).length > 0)
                    this.$validator.errors.add({
                        field: 'sharedSecret', // Not used in the traditional way, check the error div in the <template>
                        msg: 'The account identification is not valid'
                    });
            });
        },
        getAuthorizationRequired: function() {
            return this.$http.get('v1.1/authorization/required')
                .then(response => {
                    // Success callbacl
                    this.authorizationRequired = response.data.data.required;
                }, response => {
                    // Error callback
                    const notifications = JSON.parse(response.bodyText).notifications;
                    if (notifications.filter((element) => { return element.message === 'retrieving_setting_required_failed'; }).length > 0)
                        this.$validator.errors.add({
                            field: 'authorizationRequired', // Not used in the traditional way, check the error div in the <template>
                            msg: 'Could not retrieve authorization required setting, please try again later'
                        });
                });
        },
        saveAuthorizationRequired: function() {
            this.$http.post('v1.1/authorization/required', {
                required: this.authorizationRequired
            }).then(response => {
                // Success callback
                this.$parleyAdmin.queryErrors.addError(`Successfully turned authorization required <b>${(this.authorizationRequired ? 'on' : 'off')}</b>`, this.$parleyAdmin.queryErrors.TYPE_SUCCESS);
            }, response => {
                // Error callback
                let notifications = JSON.parse(response.bodyText).notifications;
                if (notifications.filter((element) => { return element.message === 'updating_setting_required_failed'; }).length > 0)
                    this.$validator.errors.add({
                        field: 'authorizationRequired', // Not used in the traditional way, check the error div in the <template>
                        msg: 'Could not save authorization required setting, please try again later'
                    });
                else if (notifications.filter((element) => { return element.message === 'identification_not_valid'; }).length > 0)
                    this.$validator.errors.add({
                        field: 'authorizationRequired', // Not used in the traditional way, check the error div in the <template>
                        msg: 'The account identification is not valid'
                    });

                this.authorizationRequired = !this.authorizationRequired;
            });
        },
        generateAuthorization: function() {
            const _generate = function(version, userIdentification, secretKey, validUntill = null) {
                let data = {
                    version: version,
                    userIdentification: userIdentification,
                    secretKey: secretKey
                };

                if (validUntill)
                    data.validUntill = validUntill;

                this.$http.post('v1.1/authorization/generate', data).then(response => {
                    // Success callback
                    this.generatedHeader = response.data.data.authorization;

                    // Warning
                    this.$refs['secretKey'].$validator.errors.clear();
                    let notifications = JSON.parse(response.bodyText).notifications;
                    if (notifications.filter((element) => { return element.message === 'secretKey_length_is_shorter_than_recommended_length'; }).length > 0)
                        this.$refs['secretKey'].$validator.errors.add({
                            field: '',
                            msg: 'Secret Key length is shorter than the recommended length of 12 characters',
                            class: 'warning'
                        });
                }, response => {
                    // Error callback
                    this.$refs['userIdentification'].$validator.errors.clear();
                    let notifications = JSON.parse(response.bodyText).notifications;
                    if (notifications.filter((element) => { return element.message === 'userIdentification_exceeded_limit_of_100'; }).length > 0)
                        this.$refs['userIdentification'].$validator.errors.add({
                            field: '',
                            msg: 'User Identification exceeded limit of 100 characters'
                        });

                    this.generatedHeader = '';
                });
            }.bind(this);

            // Validate the field first
            if (this.version === 2)
                this.$parleyAdmin.validateMultiple([this.$refs.userIdentification, this.$refs.secretKey], function(value) {
                    if (!value)
                        return;
                    _generate(2, this.userIdentification, this.secretKey);
                }.bind(this));
            else if (this.version === 3)
                this.$parleyAdmin.validateMultiple([this.$refs.userIdentification, this.$refs.secretKey, this.$refs.validUntill], function(value) {
                    if (!value)
                        return;
                    _generate(3, this.userIdentification, this.secretKey, new Date(this.validUntill).getTime() / 1000);
                }.bind(this));
        },
        validateAuthorization: function() {
            this.$parleyAdmin.validateMultiple([this.$refs.authorizationHeader], function(value) {
                if (!value)
                    return;

                this.$http.post('v1.1/authorization/validate', {
                    authorization: this.authorizationHeader,
                    version: this.version
                }).then(response => {
                    // Success callback
                    this.$parleyAdmin.queryErrors.addError('Authorization valid', this.$parleyAdmin.queryErrors.TYPE_SUCCESS);
                    // Open modal with info about authorization header
                    this.authResultUserEncryptionKey = response.data.data.authorization.userEncryptionKey;
                    this.authResultUserIdentification = response.data.data.authorization.userIdentification;
                    this.authResultValidUntill = response.data.data.authorization.validUntill;
                    this.authResultVerifyHash = response.data.data.authorization.verifyHash;
                    this.$refs['authorizationHeaderModal'].openModal();
                }, response => {
                    // Error callback
                    this.$parleyAdmin.queryErrors.addError('Authorization invalid');
                });
            }.bind(this));
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    .row {
        margin-bottom: 1em;
    }

    section {
        margin-bottom: 1em;

        .custom-input {
            padding: 0 0 1em 0;
        }

        h4 {
            padding: 0;
        }

        button {
            margin: 1.5em 0 auto;
        }
    }

    .no-margin {
        margin: unset;
    }

    #shared-secret {
        width: 80%;
        position: relative;

        input {
            width: 100%;
            padding-right: 10%;
        }

        button {
            cursor: pointer;
            color: white;
            font-size: 14px;
            position: absolute;
            top: 10px;
            right: 25px;
            background-color: #a1a5b5;
            border-style: none;
            padding: 5px 15px 5px 15px;
            border-radius: 4px;
        }
    }

    .custom-input {
        margin-right: 1em;
        padding-bottom: unset;

        &.generatedHeader,
        &.sharedSecret {
            width: 39.5em;
        }

        &.authorizationHeader {
            width: 30.5em;
        }
    }

    .errors {
        small {
            color: red;
        }
    }

    #authorizationHeaderModal {
        .content {
            width: 20em;

            .custom-input {
                padding: 0;
                textarea {
                    height: 20em;
                }
            }
        }
    }
</style>

<style lang="scss">
    #authorizationHeaderModal {
        .content {
            .custom-input {
                textarea {
                    height: 10em;
                }
            }
        }
    }
</style>
